import React, { useState, useCallback, useEffect, useRef, Suspense, lazy } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { isCarrierAccessEnabled, isVoiceChatEnabled } from "../utils/featureAccess";
import ChatInput from "../features/chat/ChatInput";
import SidebarLeft from "../layout/SidebarLeft";
import SidebarRight from "../layout/SidebarRight";
import ChatContainer from "../features/chat/ChatContainer";
import ChannelChatContainer from "../features/chat/ChannelChatContainer";
import AmpsGridPage from "../features/amps/AmpsGridPage";
import AmpChatContainer from "../features/amps/AmpChatContainer";
import LoginPage from "../features/auth/LoginPage";
import ValidationPage from "../features/auth/ValidationPage";
import SettingsPage from "../features/settings/SettingsPage";
import IntegrationsPage from "../features/settings/IntegrationsPage";
import OAuthCallbackPage from "../features/settings/OAuthCallbackPage";
import ProfilePage from "../features/profile/ProfilePage";
import VoiceDashboard from "../features/voice/VoiceDashboard";
import AmsRequiredPage from "../features/ams/AmsRequiredPage";
import MarketplacePage, { type Module } from "../features/amps/MarketplacePage";
import MacAppsPage from "../features/amps/MacAppsPage";
import QuoteProposalPage from "../features/proposals/QuoteProposalPage";
import CarrierDownloadPage from "../features/carrierdownload/CarrierDownloadPage";
import CarrierAccessPage from "../features/carrieraccess/CarrierAccessPage";
import IvansMonitorPage from "../features/ivansmonitor/IvansMonitorPage";
import DialerPage from "../features/dialer/DialerPage";
import RenewalsPage from "../features/RenewalCenter/RenewalsPage";
import CenterHomePage from "../features/centers/CenterHomePage";
import SubmissionCenterPage from "../features/centers/submission/SubmissionCenterPage";
import AddQuotePage from "../features/centers/submission/AddQuotePage";
import AddOpportunityPage from "../features/centers/submission/AddOpportunityPage";
import RateQuoteLinkPage from "../features/ratequotelink/RateQuoteLinkPage";
import CommercialLinesRatingPage from "../features/commerciallinesrating/CommercialLinesRatingPage";
import MomentumClawPage from "../features/momentumclaw/MomentumClawPage";
import BrowserAgentPage from "../features/browseragent/BrowserAgentPage";
import BrowserAgentSkillsPage from "../features/browseragent/BrowserAgentSkillsPage";
import WidgetsPage from "../features/widgets/WidgetsPage";
import CommunicationCenterPage from "../features/communication/CommunicationCenterPage";
import MomentumRoutinesPage from "../features/routines/MomentumRoutinesPage";
import TruckingAppetitePage from "../features/truckingappetite/TruckingAppetitePage";
import CarrierFinderPage from "../features/carrierfinder/CarrierFinderPage";
import CommissionScanPage from "../features/commissionscan/CommissionScanPage";
import DataQualityScanPage from "../features/dataqualityscan/DataQualityScanPage";
import CommissionForecastPage from "../features/commissionforecast/CommissionForecastPage";
import HRCenterPage from "../features/hr/HRCenterPage";
import COIScanPage from "../features/coiscan/COIScanPage";
import DriverLicenseScanPage from "../features/driverlicensescan/DriverLicenseScanPage";
import DriverLicenseScanUpload from "../features/driverlicensescan/DriverLicenseScanUpload";
import InvoiceScanPage from "../features/invoicescan/InvoiceScanPage";
import LossRunScanPage from "../features/lossrunscan/LossRunScanPage";
import MVRScanPage from "../features/mvrscan/MVRScanPage";
import MTMScanPage from "../features/mtmscan/MTMScanPage";
import VehiclePrefillPage from "../features/vehicleprefill/VehiclePrefillPage";
import UpsellIndicatorPage from "../features/upsellindicator/UpsellIndicatorPage";
import NaicsClassificationPage from "../features/naicsclassification/NaicsClassificationPage";
import ValChoiceClassificationPage from "../features/valchoiceclassification/ValChoiceClassificationPage";
import HomeOwnersPage from "../features/homeowners/HomeOwnersPage";
import FenrisPersonalLinePage from "../features/fenrispersonalline/FenrisPersonalLinePage";
import PolicyComparisonPage from "../features/policycomparison/PolicyComparisonPage";
import PolicySummaryPage from "../features/policycomparison/PolicySummaryPage";
import { installModule, uninstallModule } from "../services/marketplaceService";
import { useAuth } from "../features/auth/AuthContext";
import { ROUTES } from "./routes";
import { GuestRoute, ProtectedRoute } from "./routeGuards";
import { useChannels } from "../context/ChannelContext";
import { useAmps } from "../context/AmpContext";
import { useStatusTemplate } from "../context/StatusTemplateContext";
import { useInsuredDetail } from "../context/InsuredDetailContext";
import { useChannelDocuments } from "../context/ChannelDocumentsContext";
import { usePolicyDetail } from "../context/PolicyDetailContext";
import { useRunDetailPanel } from "../context/RunDetailPanelContext";
import { useCallDetailPanel } from "../context/CallDetailPanelContext";
import { useDialerCallDetail } from "../context/DialerCallDetailContext";
import { useFeedbackPanel } from "../context/FeedbackPanelContext";
import { useCarrierSession } from "../context/CarrierSessionContext";
import { useSubmissionCenterStats } from "../context/SubmissionCenterStatsContext";
import type {
  Channel,
  FileAttachment,
  TableData,
} from "../features/chat/chat.types";
import MarketingGuestLayout from "../features/marketing/MarketingGuestLayout";
import RequestEarlyAccessPage from "../features/marketing/RequestEarlyAccessPage";
import AppLoadingSkeleton from "../components/ui/AppLoadingSkeleton";
import StartScreen from "../features/start/StartScreen";
import AdminDashboardPage from "../features/admin/AdminDashboardPage";
import MyToolsPage from "../features/mytools/MyToolsPage";
import { LegacyEditIntegrationRedirect, LegacyIntegrationListRedirect, LegacyNewIntegrationRedirect } from "../features/apiintegrations/legacyRoutes";
import ShareResolvePage from "../features/share/ShareResolvePage";
import ShareAgentResolvePage from "../features/share/ShareAgentResolvePage";
import ShareCategoryResolvePage from "../features/share/ShareCategoryResolvePage";
import MacAIChatPage from "../features/mac-ai-chat/MacAIChatPage";
import ReviewQueuePage from "../features/reviewqueue/ReviewQueuePage";
import ClaudeAgentsPage from "../features/claudeagents/ClaudeAgentsPage";
import DigitalWorkforcePage from "../features/digitalworkforce/DigitalWorkforcePage";
import DwArtifactsPage from "../features/digitalworkforce/DwArtifactsPage";
import { bootstrapDigitalWorkforce } from "../features/digitalworkforce/dwAutoSetup";
import ArtifactsPage from "../features/artifacts/ArtifactsPage";
import ArtifactDetailPage from "../features/artifacts/ArtifactDetailPage";
import ClaudeAgentSkillsPage from "../features/claudeagents/ClaudeAgentSkillsPage";
import ClaudeAgentAgentPage from "../features/claudeagents/ClaudeAgentAgentPage";
import ClaudeAgentMcpPage from "../features/claudeagents/ClaudeAgentMcpPage";
import McpServersPage from "../features/settings/McpServersPage";
import WebhooksPage from "../features/settings/WebhooksPage";
import WebhookReportPage from "../features/settings/WebhookReportPage";
import HarnessTypeSettingsPage from "../features/settings/HarnessTypeSettingsPage";
import AgencyAdminPage from "../features/agencyadmin/AgencyAdminPage";
import MyDayPage from "../features/myday/MyDayPage";
import { ClaudeAgentsProvider } from "../features/claudeagents/ClaudeAgentsContext";
import { ActiveAgentProvider } from "../context/ActiveAgentContext";
import GlobalChatBar from "../components/chat/GlobalChatBar";
import "../features/claudeagents/claudeagents.css";
import { getDefaultPage } from "../services/userPreferencesService";
import { isMacWorkflowsEnabled, isReviewQueueEnabled } from "../utils/featureAccess";
import FenrisDriverVehiclePage from "../features/fenrisdrivervehicle/FenrisDriverVehiclePage";
import DataEnrichmentCommercialPage from "../features/dataenrichmentcommercial/DataEnrichmentCommercialPage";
import AcordSmartScanPage from "../features/acordsmartscan/AcordSmartScanPage";
import PremiumFinanceScanPage from "../features/premiumfinancescan/PremiumFinanceScanPage";

// Lazy: only this route needs the page (its CommandIssuePanel is shared and stays in main).
const MomentumCommandPage = lazy(() => import("../features/momentumcommand/MomentumCommandPage"));
// Lazy: the voice chat page and its audio code are only needed on /assistant.
const VoiceChatPage = lazy(() => import("../features/voicechat/VoiceChatPage"));

const WorkspaceLayout: React.FC<{
  sidebarOpen: boolean;
  rightSidebarOpen: boolean;
  mainCollapsed: boolean;
  onToggleSidebar: () => void;
  onCloseSidebar: () => void;
  onToggleRightSidebar: () => void;
  onToggleMainPanel: () => void;
  onAddChannel: () => void;
  onAddAmp: () => void;
  onAddClawSkill?: () => void;
  onEditClawSkill?: (skillId: string, skillName: string) => void;
  onClawNavClick?: () => void;
  onEditChannel?: (channel: Channel) => void;
  leftSidebarCollapsed?: boolean;
  onToggleLeftSidebarCollapse?: () => void;
  hideRightSidebar?: boolean;
  showCreateAgent?: boolean;
  onCloseCreateAgent?: () => void;
  showAddChannel?: boolean;
  onCloseAddChannel?: () => void;
  onAddChannelSubmit?: (
    channelData: Omit<Channel, "id" | "guid" | "createdAt">,
  ) => void;
  editingChannel?: Channel;
  onEditChannelSubmit?: (
    channelId: string,
    updates: Omit<Channel, "id" | "guid" | "createdAt">,
  ) => Promise<void>;
  marketplaceModule?: Module | null;
  onMarketplaceInstall?: () => Promise<void>;
  onMarketplaceUninstall?: () => Promise<void>;
  marketplaceIsProcessing?: boolean;
  onCloseMarketplaceDetail?: () => void;
  showCreateApiKey?: boolean;
  onCloseCreateApiKey?: () => void;
  onApiKeyCreated?: () => void;
  commissionScanData?: {
    pdf: FileAttachment;
    table: TableData;
    conversationId: string;
  } | null;
  onCloseCommissionScan?: () => void;
  showOpenClawPanel?: boolean;
  openClawConnected?: boolean;
  showBrowserAgentPanel?: boolean;
  showWidgetPanel?: boolean;
  showAddClawSkill?: boolean;
  onCloseAddClawSkill?: () => void;
  onClawSkillCreated?: () => void;
  editClawSkillData?: { name: string; skill: string } | null;
  showToolLog?: boolean;
  toolLogEntries?: unknown[];
  onCloseToolLog?: () => void;
  hideTopBar?: boolean;
  children?: React.ReactNode;
}> = ({
  sidebarOpen,
  rightSidebarOpen,
  mainCollapsed,
  onToggleSidebar,
  onCloseSidebar,
  onToggleRightSidebar,
  onToggleMainPanel,
  onAddChannel,
  onAddAmp,
  onAddClawSkill,
  onEditClawSkill,
  onClawNavClick,
  onEditChannel,
  leftSidebarCollapsed,
  onToggleLeftSidebarCollapse,
  hideRightSidebar,
  showCreateAgent,
  onCloseCreateAgent,
  showAddChannel,
  onCloseAddChannel,
  onAddChannelSubmit,
  editingChannel,
  onEditChannelSubmit,
  marketplaceModule,
  onMarketplaceInstall,
  onMarketplaceUninstall,
  marketplaceIsProcessing,
  onCloseMarketplaceDetail,
  showCreateApiKey,
  onCloseCreateApiKey,
  onApiKeyCreated,
  commissionScanData,
  onCloseCommissionScan,
  showOpenClawPanel,
  openClawConnected,
  showBrowserAgentPanel,
  showWidgetPanel,
  showAddClawSkill,
  onCloseAddClawSkill,
  onClawSkillCreated,
  editClawSkillData,
  showToolLog,
  toolLogEntries,
  onCloseToolLog,
  hideTopBar,
  children,
}) => {
  const _wsLocation = useLocation();
  const _hideGlobalChatBar = _wsLocation.pathname.includes('/import');
  // A quote/policy's detail panel has a lot more to show than the rail's default
  // width fits — widen it the same way Marketplace/API-key/commission-scan already do.
  // It gets its own (wider) class rather than sharing app-grid--right-expanded,
  // since its two-column layout needs more room than those other panels do.
  const { selected: submissionSelectedForWidth } = useSubmissionCenterStats();
  const rightSidebarSubmission = submissionSelectedForWidth?.kind === 'policy';
  const rightSidebarExpanded = !!(
    showAddChannel ||
    marketplaceModule ||
    showCreateApiKey ||
    commissionScanData
  );
  const hideRightSidebarForRate = localStorage.getItem('sourceTool') === 'Rate';
  const effectiveHideRightSidebar = hideRightSidebar || hideRightSidebarForRate;
  return (
    <div
      className={`app-grid ${mainCollapsed ? "app-grid--main-collapsed" : ""} ${!effectiveHideRightSidebar && rightSidebarOpen ? "app-grid--right-open" : ""} ${!effectiveHideRightSidebar && rightSidebarOpen && rightSidebarExpanded && !showAddChannel && !showCreateApiKey ? "app-grid--right-expanded" : ""} ${!effectiveHideRightSidebar && rightSidebarOpen && showAddChannel ? "app-grid--right-add-channel" : ""} ${!effectiveHideRightSidebar && rightSidebarOpen && showCreateApiKey ? "app-grid--right-create-apikey" : ""} ${!effectiveHideRightSidebar && rightSidebarOpen && rightSidebarSubmission ? "app-grid--right-submission" : ""}`}
    >
      <SidebarLeft
        isOpen={sidebarOpen}
        onClose={onCloseSidebar}
        onAddChannel={onAddChannel}
        onAddAmp={onAddAmp}
        onAddClawSkill={onAddClawSkill}
        onEditClawSkill={onEditClawSkill}
        onClawNavClick={onClawNavClick}
        onEditChannel={onEditChannel}
        collapsed={leftSidebarCollapsed}
        onToggleCollapse={onToggleLeftSidebarCollapse}
      />
      <main
        className={`center-panel ${mainCollapsed ? "center-panel--collapsed" : ""}`}
      >
        {/* The page gets everything above the chat dock — the dock is its own
            row below it, not an overlay — so the bar never covers content. */}
        <div className="center-panel__content">
          {children || <ChatContainer />}
        </div>

        {!mainCollapsed && !_hideGlobalChatBar && (
          <div className="global-chat-bar-dock">
            <GlobalChatBar />
          </div>
        )}

        {/* Collapsed Icon Panel */}
        <div className="center-panel__collapsed-content">
          {/* Expand Icon - Top */}
          <div
            className="center-panel__collapsed-icon center-panel__collapsed-icon--expand"
            onClick={onToggleMainPanel}
            title="Expand main panel"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="9 18 15 12 9 6"></polyline>
            </svg>
          </div>
        </div>
      </main>
      {!effectiveHideRightSidebar && (
        <SidebarRight
          isOpen={rightSidebarOpen}
          onToggle={onToggleRightSidebar}
          onToggleMainPanel={onToggleMainPanel}
          mainCollapsed={mainCollapsed}
          showCreateAgent={showCreateAgent}
          onCloseCreateAgent={onCloseCreateAgent}
          showAddChannel={showAddChannel}
          onCloseAddChannel={onCloseAddChannel}
          onAddChannelSubmit={onAddChannelSubmit}
          editingChannel={editingChannel}
          onEditChannelSubmit={onEditChannelSubmit}
          marketplaceModule={marketplaceModule}
          onMarketplaceInstall={onMarketplaceInstall}
          onMarketplaceUninstall={onMarketplaceUninstall}
          marketplaceIsProcessing={marketplaceIsProcessing}
          onCloseMarketplaceDetail={onCloseMarketplaceDetail}
          showCreateApiKey={showCreateApiKey}
          onCloseCreateApiKey={onCloseCreateApiKey}
          onApiKeyCreated={onApiKeyCreated}
          commissionScanData={commissionScanData}
          onCloseCommissionScan={onCloseCommissionScan}
          showOpenClawPanel={showOpenClawPanel}
          openClawConnected={openClawConnected}
          showBrowserAgentPanel={showBrowserAgentPanel}
          showWidgetPanel={showWidgetPanel}
          showAddClawSkill={showAddClawSkill}
          onCloseAddClawSkill={onCloseAddClawSkill}
          onClawSkillCreated={onClawSkillCreated}
          editClawSkillData={editClawSkillData}
          showToolLog={showToolLog}
          toolLogEntries={toolLogEntries}
          onCloseToolLog={onCloseToolLog}
        />
      )}
    </div>
  );
};

const AppShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAMSUser, isAuthenticated, isLoading: authLoading } = useAuth();
  // Digital Workforce is on for every agency: provision the signed-in user's
  // agents in the background right after sign-in, instead of waiting for them
  // to open the Digital Workforce page and click "launch setup". The page
  // itself re-runs this (with a visible progress/error state) if it hasn't
  // finished by the time they get there. See dwAutoSetup.ts.
  useEffect(() => {
    if (!isAuthenticated) return;
    bootstrapDigitalWorkforce();
  }, [isAuthenticated]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(false);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(
    () => localStorage.getItem('sourceTool') !== 'Rate',
  ); // default expanded, unless opened from the Rate source tool
  const [mainCollapsed, setMainCollapsed] = useState(false);
  const [channelModalOpen, setChannelModalOpen] = useState(false);
  const [editingChannel, setEditingChannel] = useState<Channel | undefined>(
    undefined,
  );
  const [createAgentOpen, setCreateAgentOpen] = useState(false);
  const prevPathname = useRef<string | null>(null);
  const {
    createChannel,
    updateChannel,
    channels,
    loading: channelsLoading,
    getChannel,
  } = useChannels();
  const { amps, ensureAmpsLoaded, initialLoading: ampsInitialLoading } = useAmps();
  // AMP lists load on demand. A route whose AMP isn't known yet (first visit to an
  // /amp or /channel page) parks its "is this the Certificates AMP?" sidebar
  // decision here until the lists arrive — see the effect below.
  const [pendingAmpSidebarCheck, setPendingAmpSidebarCheck] = useState<{ path: string; ampId: string } | null>(null);
  const { statusTemplate } = useStatusTemplate();
  const { selectedInsuredId, setSelectedInsuredId } = useInsuredDetail();
  const { showChannelDocPanel, openEventId, closeChannelDocPanel } =
    useChannelDocuments();
  const { selectedPolicyId, setSelectedPolicyId } = usePolicyDetail();
  const { setRunDetailPanel } = useRunDetailPanel();
  const { setCallDetailPanel } = useCallDetailPanel();
  const { setDialerCallDetail } = useDialerCallDetail();
  const { setFeedbackPanel, feedbackPanel } = useFeedbackPanel();
  const { sessionPanel, closeSession } = useCarrierSession();
  const { selected: submissionSelected, setSelected: setSubmissionSelected, quoteAddedSignal } = useSubmissionCenterStats();

  const AUTH_ROUTES = ["/login", "/hubvalidation"];

  const NO_DOCK_PATHS = [
    "/login",
    "/hubvalidation",
    "/start",
    "/request-early-access",
  ];
  const showFloatingDock =
    isAuthenticated &&
    !NO_DOCK_PATHS.some(
      (p) => location.pathname === p || location.pathname.startsWith(p + "/"),
    ) &&
    mainCollapsed;

  const handleFloatingDockSend = useCallback(
    (message: string) => {
      const newId = crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      navigate(`${ROUTES.CHAT}/${newId}`, {
        state: { initialMessage: message },
      });
    },
    [navigate],
  );

  // Full-screen skeleton shown while waiting for the welcome channel redirect.
  // Initialized from localStorage so SSO hard-reloads start covered immediately.
  const [showWelcomeSkeleton, setShowWelcomeSkeleton] = useState(() => {
    return localStorage.getItem("pendingWelcomeNav") === "true";
  });

  // Navigate to Welcome channel after login
  // Also triggered by the SSO validation flow via localStorage flag
  const [pendingWelcomeNav, setPendingWelcomeNav] = useState(() => {
    const flag = localStorage.getItem("pendingWelcomeNav") === "true";
    if (flag) localStorage.removeItem("pendingWelcomeNav");
    return flag;
  });
  // Prevents collapse on the /chat → /channel redirect triggered by welcome nav
  const skipNextCollapseRef = useRef(false);

  useEffect(() => {
    if (!pendingWelcomeNav) return;
    if (channelsLoading) return;
    setPendingWelcomeNav(false);

    const doWelcomeChannel = () => {
      const showWelcome =
        localStorage.getItem("showWelcomeChannel") !== "false";
      const welcome = showWelcome
        ? channels.find((c) => c.name?.toLowerCase() === "welcome")
        : null;
      if (welcome) {
        setRightSidebarOpen(true);
        skipNextCollapseRef.current = true;
        navigate(`/channel/${welcome.guid}`, { replace: true });
        setTimeout(() => setShowWelcomeSkeleton(false), 350);
      } else {
        setShowWelcomeSkeleton(false);
      }
    };

    // LoginPage already called getDefaultPage() — use its result directly
    const preFetched = localStorage.getItem("postLoginDefaultPath");
    if (preFetched !== null) {
      localStorage.removeItem("postLoginDefaultPath");
      if (preFetched) {
        // Has a default path — navigate there if not already
        if (location.pathname !== preFetched)
          navigate(preFetched, { replace: true });
        setShowWelcomeSkeleton(false);
      } else {
        // LoginPage found no default page — go to welcome channel
        doWelcomeChannel();
      }
      return;
    }

    // SSO / hubvalidation flow: LoginPage didn't run, call API now
    (async () => {
      const userDefaultPath = await getDefaultPage();
      if (userDefaultPath) {
        navigate(userDefaultPath, { replace: true });
        setShowWelcomeSkeleton(false);
        return;
      }
      doWelcomeChannel();
    })();
  }, [
    pendingWelcomeNav,
    channelsLoading,
    channels,
    navigate,
    location.pathname,
  ]);

  // Collapse right sidebar and close all panels on route change (skip initial load and post-login redirect)
  useEffect(() => {
    const prev = prevPathname.current;
    prevPathname.current = location.pathname;

    // First render: skip
    if (prev === null) return;

    if (AUTH_ROUTES.includes(prev)) {
      if (!location.pathname.startsWith("/claude-agents")) {
        setRightSidebarOpen(true);
      }
      if (isAuthenticated) {
        setPendingWelcomeNav(true);
        setShowWelcomeSkeleton(true);
      }
      return;
    }

    // Skip collapse for the post-login welcome channel redirect
    if (skipNextCollapseRef.current) {
      skipNextCollapseRef.current = false;
      return;
    }

    const getSection = (p: string) =>
      p.split("/").filter(Boolean).slice(0, 2).join("/");
    if (prev && getSection(prev) === getSection(location.pathname)) return;

    // Check if the new route is a Certificates AMP (direct or channel)
    const isMomentumClaw = location.pathname.startsWith("/momentum-claw");
    const ampRouteId = location.pathname.match(/^\/amp\/([\w:.-]+)/)?.[1];
    const channelGuid = location.pathname.match(/^\/channel\/([\w-]+)/)?.[1];
    const ampFromQuery = new URLSearchParams(location.search).get("amp");
    const channelObj = channelGuid ? getChannel(channelGuid) : null;
    const activeAmpId = ampRouteId ?? ampFromQuery ?? channelObj?.ampIds?.[0];
    const activeAmp = activeAmpId
      ? amps.find((a) => a.id === activeAmpId)
      : null;
    const isCertificates = activeAmp?.name === "Certificates";

    const isDwArtifacts    = location.pathname === '/dw-artifacts' || location.pathname === '/digital-workforce';
    const isKanbanRoute    = location.pathname.startsWith('/momentum-kanban');
    const isArtifactsRoute = location.pathname.startsWith('/artifacts');
    const isRoutinesRoute  = location.pathname.startsWith(ROUTES.MOMENTUM_ROUTINES);
    const isCarrierDownloadRoute = location.pathname.startsWith(ROUTES.CARRIER_DOWNLOAD);
    const isVoiceRoute     = location.pathname.startsWith('/voice');
    const isDialerRoute    = location.pathname.startsWith(ROUTES.DIALER);

    setPendingAmpSidebarCheck(null);
    if (isDwArtifacts || isKanbanRoute || isArtifactsRoute || isRoutinesRoute || isCarrierDownloadRoute || isVoiceRoute || isDialerRoute) {
      setRightSidebarOpen(true);
    } else if (!isMomentumClaw && activeAmpId && !activeAmp) {
      // AMP lists not loaded yet — load them, then decide (Certificates keeps the sidebar).
      setPendingAmpSidebarCheck({ path: location.pathname, ampId: activeAmpId });
      void ensureAmpsLoaded();
    } else if (isMomentumClaw || !isCertificates) {
      setRightSidebarOpen(false);
    }

    setChannelModalOpen(false);
    setCreateApiKeyOpen(false);
    setSelectedMarketplaceModule(null);
    setCommissionScanData(null);
    setAddClawSkillOpen(false);
    setEditClawSkillData(null);
    setFeedbackPanel(null);
    closeChannelDocPanel();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Auto-open right sidebar when feedback/mcp panel is triggered
  useEffect(() => {
    if (feedbackPanel) setRightSidebarOpen(true);
  }, [feedbackPanel]);

  // Auto-open right sidebar when a status template arrives
  useEffect(() => {
    if (statusTemplate) setRightSidebarOpen(true);
  }, [statusTemplate]);

  // Auto-open right sidebar when an insured is selected
  useEffect(() => {
    if (selectedInsuredId) setRightSidebarOpen(true);
  }, [selectedInsuredId]);

  // Auto-open right sidebar when Documents panel is triggered (openEventId fires on every open call)
  useEffect(() => {
    if (showChannelDocPanel) setRightSidebarOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChannelDocPanel, openEventId]);

  // Auto-open right sidebar when a policy is selected
  useEffect(() => {
    if (selectedPolicyId) setRightSidebarOpen(true);
  }, [selectedPolicyId]);

  // Auto-open right sidebar when a quote/policy is picked in Submission Center
  useEffect(() => {
    if (submissionSelected) setRightSidebarOpen(true);
  }, [submissionSelected]);

  // Auto-close right sidebar once a quote is successfully saved from the "Add Quote"
  // panel — quoteAddedSignal starts at 0, so this is naturally a no-op on mount.
  useEffect(() => {
    if (quoteAddedSignal) setRightSidebarOpen(false);
  }, [quoteAddedSignal]);

  // Auto-open right sidebar when a carrier session detail is opened
  useEffect(() => {
    if (sessionPanel) setRightSidebarOpen(true);
  }, [sessionPanel]);

  // Reset insured/policy/run-detail/session sidebar on every route change —
  // these are all transient, page-scoped right-sidebar overlays (e.g.
  // Command Center's "View Details") that must not survive navigating to a
  // different page.
  useEffect(() => {
    setSelectedInsuredId(null);
    setSelectedPolicyId(null);
    setRunDetailPanel(null);
    setCallDetailPanel(null);
    setDialerCallDetail(null);
    closeSession();
  }, [location.pathname, location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // Global ".ai" hotkey — navigate to /start from anywhere
  useEffect(() => {
    const buffer = { value: "" };
    const SEQUENCE = ".ai";
    const TIMEOUT_MS = 1500;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const onKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName?.toLowerCase();
      const isEditable =
        tag === "input" ||
        tag === "textarea" ||
        (e.target as HTMLElement)?.isContentEditable;
      if (isEditable) {
        buffer.value = "";
        return;
      }

      if (e.key.length !== 1) {
        buffer.value = "";
        return;
      }

      buffer.value += e.key.toLowerCase();
      if (buffer.value.length > SEQUENCE.length) {
        buffer.value = buffer.value.slice(-SEQUENCE.length);
      }

      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        buffer.value = "";
      }, TIMEOUT_MS);

      if (buffer.value === SEQUENCE) {
        buffer.value = "";
        navigate(ROUTES.START);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timer) clearTimeout(timer);
    };
  }, [navigate]);

  // Finishes a route-change sidebar decision that was waiting on the AMP lists.
  useEffect(() => {
    if (!pendingAmpSidebarCheck) return;
    if (location.pathname !== pendingAmpSidebarCheck.path) {
      setPendingAmpSidebarCheck(null);
      return;
    }
    const amp = amps.find((a) => a.id === pendingAmpSidebarCheck.ampId);
    if (!amp && ampsInitialLoading) return; // still loading
    if (amp?.name !== "Certificates") setRightSidebarOpen(false);
    setPendingAmpSidebarCheck(null);
  }, [pendingAmpSidebarCheck, amps, ampsInitialLoading, location.pathname]);

  // Collapse right sidebar by default for Momentum Claw page
  useEffect(() => {
    if (location.pathname.startsWith("/momentum-claw")) {
      setRightSidebarOpen(false);
    }
  }, [location.pathname]);

  // Collapse right sidebar by default for Personal Line Rating page —
  // the table needs maximum width; sidebar opens on demand (row click, Execute).
  useEffect(() => {
    if (location.pathname.startsWith("/rate-quote-link") || location.pathname.startsWith("/rate-quoting") || location.pathname.startsWith("/commercial-lines-rating")) {
      setRightSidebarOpen(false);
    }
  }, [location.pathname]);

  // Auto-open right sidebar on MAC Workflows page
  useEffect(() => {
    if (location.pathname.startsWith("/mac-workflows")) {
      setRightSidebarOpen(true);
    }
  }, [location.pathname]);

  // Auto-open right sidebar on Settings → Webhooks (and its report page) — the
  // sidebar holds the Webhook Analytics panel, or one call's details when a row
  // on the report is picked (?call=<id>; reopens it if the user had closed it).
  // Runs after the route-change collapse above, so it wins; also covers a direct
  // load/refresh.
  const webhookCallParam = new URLSearchParams(location.search).get("call");
  useEffect(() => {
    if (location.pathname.startsWith(ROUTES.SETTINGS_WEBHOOKS)) {
      setRightSidebarOpen(true);
    }
  }, [location.pathname, webhookCallParam]);

  // Trigger resize events during/after sidebar transition so recharts re-measures
  useEffect(() => {
    const delays = [50, 120, 200, 350];
    const timers = delays.map((d) =>
      setTimeout(() => window.dispatchEvent(new Event("resize")), d),
    );
    return () => timers.forEach(clearTimeout);
  }, [rightSidebarOpen]);

  const toggleSidebar = useCallback(() => setSidebarOpen((p) => !p), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Toggle left sidebar collapse — guard: at least one of the three panels must stay expanded
  const toggleLeftSidebarCollapse = useCallback(() => {
    setLeftSidebarCollapsed((prev) => {
      const newCollapsed = !prev;
      if (newCollapsed) {
        // Left is collapsing: ensure center or right is expanded
        if (mainCollapsed && !rightSidebarOpen) {
          setMainCollapsed(false); // expand center panel
        }
      }
      return newCollapsed;
    });
  }, [mainCollapsed, rightSidebarOpen]);

  const toggleRightSidebar = useCallback(() => {
    setRightSidebarOpen((prevRightOpen) => {
      const newRightState = !prevRightOpen;
      if (!newRightState) {
        // Right is closing: ensure center or left is expanded
        if (mainCollapsed) {
          setMainCollapsed(false);
        }
        if (leftSidebarCollapsed) {
          setLeftSidebarCollapsed(false);
        }
        // Also drop the Submission Center quote/policy selection so the rail's
        // width mechanism (rightSidebarExpanded in WorkspaceLayout) collapses
        // back to its regular width instead of staying wide the next time it opens.
        setSubmissionSelected(null);
      }
      return newRightState;
    });
  }, [mainCollapsed, leftSidebarCollapsed, setSubmissionSelected]);

  // "Pin to sidebar" on a widget switches the sidebar's widget panel (WidgetSidebarPanel listens
  // for the same event); open the rail too, or a collapsed sidebar made the button do nothing.
  useEffect(() => {
    const open = () => setRightSidebarOpen(true);
    window.addEventListener('widgets:open-in-sidebar', open);
    return () => window.removeEventListener('widgets:open-in-sidebar', open);
  }, []);

  const toggleMainPanel = useCallback(() => {
    setMainCollapsed((p) => {
      const newState = !p;
      if (newState) {
        // Center is collapsing: ensure right sidebar is expanded
        setRightSidebarOpen(true);
        // Also expand left sidebar if it was collapsed
        if (leftSidebarCollapsed) {
          setLeftSidebarCollapsed(false);
        }
      }
      return newState;
    });
  }, [leftSidebarCollapsed]);

  const openChannelModal = useCallback(() => {
    setCreateApiKeyOpen(false);
    setSelectedMarketplaceModule(null);
    setEditingChannel(undefined);
    setChannelModalOpen(true);
    setRightSidebarOpen(true);
  }, []);
  const closeChannelModal = useCallback(() => {
    setChannelModalOpen(false);
    setEditingChannel(undefined);
  }, []);

  const openEditChannel = useCallback((channel: Channel) => {
    setEditingChannel(channel);
    setChannelModalOpen(true);
    setRightSidebarOpen(true);
  }, []);

  const handleEditChannel = useCallback(
    async (
      channelId: string,
      updates: Omit<Channel, "id" | "guid" | "createdAt">,
    ) => {
      try {
        await updateChannel(channelId, updates);
        setChannelModalOpen(false);
        setEditingChannel(undefined);
      } catch (err) {
        console.error("Failed to update channel:", err);
        throw err;
      }
    },
    [updateChannel],
  );

  // Allow any child (e.g. WelcomeChannelPage) to open the Add Channel panel
  useEffect(() => {
    const handler = () => openChannelModal();
    window.addEventListener("openAddChannel", handler);
    return () => window.removeEventListener("openAddChannel", handler);
  }, [openChannelModal]);

  // Allow Claude Agents list page to open the Create Agent panel
  const openCreateAgent = useCallback(() => {
    setChannelModalOpen(false);
    setCreateAgentOpen(true);
    setRightSidebarOpen(true);
  }, []);
  const closeCreateAgent = useCallback(() => setCreateAgentOpen(false), []);
  useEffect(() => {
    window.addEventListener("openCreateAgent", openCreateAgent);
    return () => window.removeEventListener("openCreateAgent", openCreateAgent);
  }, [openCreateAgent]);

  // ── Tool Log sidebar state ────────────────────────────────────────────────
  const [showToolLog, setShowToolLog] = useState(false);
  const [toolLogEntries, setToolLogEntries] = useState<unknown[]>([]);
  const closeToolLog = useCallback(() => setShowToolLog(false), []);
  useEffect(() => {
    const showHandler = (e: Event) => {
      const entries =
        (e as CustomEvent<{ entries: unknown[] }>).detail?.entries ?? [];
      setToolLogEntries(entries);
      setShowToolLog(true);
      setRightSidebarOpen(true);
    };
    const updateHandler = (e: Event) => {
      const entries =
        (e as CustomEvent<{ entries: unknown[] }>).detail?.entries ?? [];
      setToolLogEntries(entries);
    };
    const clearHandler = () => {
      setToolLogEntries([]);
      setShowToolLog(false);
    };
    window.addEventListener("showToolLogPanel", showHandler);
    window.addEventListener("updateToolLog", updateHandler);
    window.addEventListener("clearToolLog", clearHandler);
    return () => {
      window.removeEventListener("showToolLogPanel", showHandler);
      window.removeEventListener("updateToolLog", updateHandler);
      window.removeEventListener("clearToolLog", clearHandler);
    };
  }, []);

  const navigateToCreateAmp = useCallback(() => {
    setChannelModalOpen(false);
    setCreateApiKeyOpen(false);
    setSelectedMarketplaceModule(null);
    navigate(ROUTES.AMPS_CREATE);
  }, [navigate]);

  // ── Create API Key sidebar state ─────────────────────────────────────────
  const [createApiKeyOpen, setCreateApiKeyOpen] = useState(false);
  const [apiKeyRefreshTrigger, setApiKeyRefreshTrigger] = useState(0);

  const openCreateApiKey = useCallback(() => {
    setChannelModalOpen(false);
    setSelectedMarketplaceModule(null);
    setCreateApiKeyOpen(true);
    setRightSidebarOpen(true);
  }, []);

  const closeCreateApiKey = useCallback(() => {
    setCreateApiKeyOpen(false);
  }, []);

  const handleApiKeyCreated = useCallback(() => {
    setApiKeyRefreshTrigger((t) => t + 1);
  }, []);

  // ── Commission Scan sidebar state ────────────────────────────────────────
  const [commissionScanData, setCommissionScanData] = useState<{
    pdf: FileAttachment;
    table: TableData;
    conversationId: string;
  } | null>(null);

  const openCommissionScan = useCallback(
    (pdf: FileAttachment, table: TableData, conversationId: string) => {
      setChannelModalOpen(false);
      setCreateApiKeyOpen(false);
      setSelectedMarketplaceModule(null);
      setCommissionScanData({ pdf, table, conversationId });
      setRightSidebarOpen(true);
    },
    [],
  );

  const closeCommissionScan = useCallback(
    () => setCommissionScanData(null),
    [],
  );

  // ── Add Claw Skill sidebar state ─────────────────────────────────────────
  const [addClawSkillOpen, setAddClawSkillOpen] = useState(false);
  const [clawSkillRefresh, setClawSkillRefresh] = useState(0);
  const [editClawSkillData, setEditClawSkillData] = useState<{
    name: string;
    skill: string;
  } | null>(null);

  const openAddClawSkill = useCallback(() => {
    setEditClawSkillData(null);
    setRightSidebarOpen(true);
    setAddClawSkillOpen(true);
  }, []);

  const openEditClawSkill = useCallback(
    async (skillId: string, skillName: string) => {
      setEditClawSkillData({ name: skillName, skill: "" });
      setRightSidebarOpen(true);
      setAddClawSkillOpen(true);
      const { fetchClawSkillById } =
        await import("../services/momentumClawService");
      const data = await fetchClawSkillById(skillId);
      if (data) {
        setEditClawSkillData({
          name: data.name || skillName,
          skill: data.skill,
        });
      }
    },
    [],
  );

  const closeAddClawSkill = useCallback(() => {
    setAddClawSkillOpen(false);
    setEditClawSkillData(null);
  }, []);

  const handleClawSkillCreated = useCallback(() => {
    setClawSkillRefresh((n) => n + 1);
  }, []);

  // ── Marketplace sidebar state (mirrors voice-edit pattern) ──────────────
  const [selectedMarketplaceModule, setSelectedMarketplaceModule] =
    useState<Module | null>(null);
  const [marketplaceSidebarProcessing, setMarketplaceSidebarProcessing] =
    useState(false);

  const openMarketplaceDetail = useCallback((module: Module) => {
    setChannelModalOpen(false);
    setCreateApiKeyOpen(false);
    setSelectedMarketplaceModule(module);
    setRightSidebarOpen(true);
  }, []);

  const closeMarketplaceDetail = useCallback(() => {
    setSelectedMarketplaceModule(null);
  }, []);

  const handleMarketplaceSidebarInstall = useCallback(async () => {
    if (!selectedMarketplaceModule) return;
    setMarketplaceSidebarProcessing(true);
    try {
      await installModule(selectedMarketplaceModule.moduleId, true);
      setSelectedMarketplaceModule((prev) =>
        prev ? { ...prev, installed: true, isEnabled: true } : null,
      );
    } finally {
      setMarketplaceSidebarProcessing(false);
    }
  }, [selectedMarketplaceModule]);

  const handleMarketplaceSidebarUninstall = useCallback(async () => {
    if (!selectedMarketplaceModule) return;
    setMarketplaceSidebarProcessing(true);
    try {
      await uninstallModule(selectedMarketplaceModule.moduleId);
      setSelectedMarketplaceModule((prev) =>
        prev ? { ...prev, installed: false, isEnabled: false } : null,
      );
    } finally {
      setMarketplaceSidebarProcessing(false);
    }
  }, [selectedMarketplaceModule]);

  const handleAddChannel = useCallback(
    async (channelData: Omit<Channel, "id" | "guid" | "createdAt">) => {
      try {
        const newChannel = await createChannel(channelData);
        navigate(`/channel/${newChannel.guid}`);
      } catch (err) {
        console.error("Failed to create channel:", err);
        throw err;
      }
    },
    [createChannel, navigate],
  );

  return (
    <ClaudeAgentsProvider>
      <ActiveAgentProvider>
      <div className="app-root">
        <div className="app-glow-layer" />

        <Routes>
          <Route
            path={ROUTES.LOGIN}
            element={
              <GuestRoute>
                <LoginPage />
              </GuestRoute>
            }
          />
          <Route
            path={ROUTES.SIGNUP}
            element={<Navigate to={ROUTES.LOGIN} replace />}
          />

          {/* ── Start Screen ── */}
          <Route
            path={ROUTES.HOME}
            element={
              isAuthenticated ? (
                <Navigate to={ROUTES.START} replace />
              ) : (
                <Navigate to={ROUTES.CHAT} replace />
              )
            }
          />
          <Route
            path={ROUTES.START}
            element={
              authLoading ? null : isAuthenticated ? (
                <StartScreen />
              ) : (
                <Navigate to={ROUTES.CHAT} replace />
              )
            }
          />

          {/* ── Guest Marketing Layout (shown when not authenticated) ── */}
          <Route
            path={ROUTES.CHAT}
            element={
              !isAuthenticated ? (
                <MarketingGuestLayout />
              ) : (
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                />
              )
            }
          />
          {/* Original /chat route (commented out — replaced by conditional above):
        <Route
          path={ROUTES.CHAT}
          element={
            <WorkspaceLayout
              sidebarOpen={sidebarOpen}
              rightSidebarOpen={rightSidebarOpen}
              mainCollapsed={mainCollapsed}
              onToggleSidebar={toggleSidebar}
              onCloseSidebar={closeSidebar}
              onToggleRightSidebar={toggleRightSidebar}
              onToggleMainPanel={toggleMainPanel}
              leftSidebarCollapsed={leftSidebarCollapsed}
              onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
              onAddChannel={openChannelModal}
              onAddAmp={navigateToCreateAmp}
              onEditChannel={openEditChannel}
              showAddChannel={channelModalOpen}
              onCloseAddChannel={closeChannelModal}
              onAddChannelSubmit={handleAddChannel}
              editingChannel={editingChannel}
              onEditChannelSubmit={handleEditChannel}
            />
          }
        />
        */}
          <Route
            path={ROUTES.CHAT_CONVERSATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                commissionScanData={commissionScanData}
                onCloseCommissionScan={closeCommissionScan}
              >
                <ChatContainer
                  onCommissionScan={openCommissionScan}
                  onResetScan={closeCommissionScan}
                />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CHANNEL}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                commissionScanData={commissionScanData}
                onCloseCommissionScan={closeCommissionScan}
              >
                <ChannelChatContainer
                  onCommissionScan={openCommissionScan}
                  onResetScan={closeCommissionScan}
                />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CHANNEL_CONVERSATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                commissionScanData={commissionScanData}
                onCloseCommissionScan={closeCommissionScan}
              >
                <ChannelChatContainer
                  onCommissionScan={openCommissionScan}
                  onResetScan={closeCommissionScan}
                />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.AMPS_CREATE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <AmpsGridPage initialCreateMode />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.AMPS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <AmpsGridPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.AMP_CHAT}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                commissionScanData={commissionScanData}
                onCloseCommissionScan={closeCommissionScan}
              >
                <AmpChatContainer
                  onCommissionScan={openCommissionScan}
                  onResetScan={closeCommissionScan}
                />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.AMP_CONVERSATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                commissionScanData={commissionScanData}
                onCloseCommissionScan={closeCommissionScan}
              >
                <AmpChatContainer
                  onCommissionScan={openCommissionScan}
                  onResetScan={closeCommissionScan}
                />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.DASHBOARD}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              />
            }
          />
          <Route
            path={ROUTES.SETTINGS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <SettingsPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.SETTINGS_INTEGRATIONS_CALLBACK}
            element={<OAuthCallbackPage />}
          />
          <Route
            path={ROUTES.SETTINGS_INTEGRATIONS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
              >
                <IntegrationsPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.PROFILE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                showCreateApiKey={createApiKeyOpen}
                onCloseCreateApiKey={closeCreateApiKey}
                onApiKeyCreated={handleApiKeyCreated}
              >
                <ProfilePage
                  onOpenCreateApiKey={openCreateApiKey}
                  refreshApiKeysTrigger={apiKeyRefreshTrigger}
                />
              </WorkspaceLayout>
            }
          />

          <Route
            path={ROUTES.VOICE}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                >
                  {isAMSUser ? (
                    <VoiceDashboard />
                  ) : (
                    <AmsRequiredPage featureName="Momentum Voice" />
                  )}
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path={`${ROUTES.QUOTE_PROPOSAL}/*`}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                {isAMSUser ? (
                  <QuoteProposalPage />
                ) : (
                  <AmsRequiredPage featureName="Quote Proposal Generator" />
                )}
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CARRIER_DOWNLOAD}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CarrierDownloadPage />
              </WorkspaceLayout>
            }
          />
          {/* '/center/submission' itself falls through to the dynamic Center
              route below (the generic launcher, like every other Center) —
              the tracker is its own workspace route, one level under it. */}
          <Route
            path={ROUTES.CENTER_SUBMISSION_TRACKER}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <SubmissionCenterPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CENTER_SUBMISSION_ADD_QUOTE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <AddQuotePage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CENTER_SUBMISSION_ADD_OPPORTUNITY}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <AddOpportunityPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CENTER_HOME}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CenterHomePage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CARRIER_ACCESS}
            element={
              isCarrierAccessEnabled() ? (
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <CarrierAccessPage />
                </WorkspaceLayout>
              ) : (
                <Navigate to={ROUTES.HOME} replace />
              )
            }
          />
          <Route
            path={ROUTES.IVANS_MONITOR}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <IvansMonitorPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.DIALER}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <DialerPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.RENEWAL_CENTER}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <RenewalsPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.RATE_QUOTE_LINK}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <RateQuoteLinkPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={`${ROUTES.RATE_QUOTING}/*`}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <RateQuoteLinkPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={`${ROUTES.COMMERCIAL_LINES_RATING}/*`}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CommercialLinesRatingPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.TRUCKING_APPETITE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <TruckingAppetitePage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.TRUCKING_APPETITE_CONVERSATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <TruckingAppetitePage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CARRIER_FINDER}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CarrierFinderPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.CARRIER_FINDER_CONVERSATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CarrierFinderPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.COMMISSION_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CommissionScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.COI_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <COIScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.DRIVER_LICENSE_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <DriverLicenseScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.INVOICE_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <InvoiceScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.ACORD_DEC_PAGES_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <AcordSmartScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.PREMFI_PFA_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <PremiumFinanceScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.COMMISSION_FORECAST}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CommissionForecastPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.HR_CENTER}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <HRCenterPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.LOSS_RUN_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <LossRunScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.MVR_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <MVRScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.MTM_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <MTMScanPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.VEHICLE_PREFILL}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <VehiclePrefillPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.NAICS_CLASSIFICATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <NaicsClassificationPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.VALCHOICE_CLASSIFICATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <ValChoiceClassificationPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.UPSELL_INDICATOR}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <UpsellIndicatorPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.MOMENTUM_CLAW}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                showOpenClawPanel
                openClawConnected={false}
                onAddClawSkill={openAddClawSkill}
                onEditClawSkill={openEditClawSkill}
                onClawNavClick={closeAddClawSkill}
                showAddClawSkill={addClawSkillOpen}
                onCloseAddClawSkill={closeAddClawSkill}
                onClawSkillCreated={handleClawSkillCreated}
                editClawSkillData={editClawSkillData}
              >
                <MomentumClawPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.MOMENTUM_CLAW_CONVERSATION}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                showOpenClawPanel
                openClawConnected={true}
                onAddClawSkill={openAddClawSkill}
                onEditClawSkill={openEditClawSkill}
                onClawNavClick={closeAddClawSkill}
                showAddClawSkill={addClawSkillOpen}
                onCloseAddClawSkill={closeAddClawSkill}
                onClawSkillCreated={handleClawSkillCreated}
                editClawSkillData={editClawSkillData}
              >
                <MomentumClawPage />
              </WorkspaceLayout>
            }
          />

          <Route
            path={ROUTES.BROWSER_AGENT}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                showBrowserAgentPanel
              >
                <BrowserAgentPage />
              </WorkspaceLayout>
            }
          />

          {/* ── Browser Agent — Skills full page ── */}
          <Route
            path={ROUTES.BROWSER_AGENT_SKILLS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <BrowserAgentSkillsPage />
              </WorkspaceLayout>
            }
          />

          {/* ── Widgets — installed sandboxed widgets (grid + full-size detail) ── */}
          {[ROUTES.WIDGETS, ROUTES.WIDGET_DETAIL].map(path => (
            <Route
              key={path}
              path={path}
              element={
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showWidgetPanel
                >
                  <WidgetsPage />
                </WorkspaceLayout>
              }
            />
          ))}

          {/* Single route with an optional :taskId param — covers both the bare
              board URL and the per-task detail URL so navigating between them
              (e.g. right after a brand-new task's first save) never causes
              React Router to swap route matches and remount this page mid-stream. */}
          <Route
            path={ROUTES.MOMENTUM_COMMAND_DETAIL}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                hideTopBar
              >
                <Suspense fallback={null}>
                  <MomentumCommandPage />
                </Suspense>
              </WorkspaceLayout>
            }
          />

          {/* ── Voice chat — gated per agency while it rolls out (REACT_APP_VOICE_CHAT_AGENCIES) ── */}
          <Route
            path={ROUTES.ASSISTANT_CONVERSATION}
            element={
              isVoiceChatEnabled() ? (
                <ProtectedRoute>
                  <WorkspaceLayout
                    sidebarOpen={sidebarOpen}
                    rightSidebarOpen={rightSidebarOpen}
                    mainCollapsed={mainCollapsed}
                    onToggleSidebar={toggleSidebar}
                    onCloseSidebar={closeSidebar}
                    onToggleRightSidebar={toggleRightSidebar}
                    onToggleMainPanel={toggleMainPanel}
                    leftSidebarCollapsed={leftSidebarCollapsed}
                    onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                    onAddChannel={openChannelModal}
                    onAddAmp={navigateToCreateAmp}
                    onEditChannel={openEditChannel}
                    showAddChannel={channelModalOpen}
                    onCloseAddChannel={closeChannelModal}
                    onAddChannelSubmit={handleAddChannel}
                    editingChannel={editingChannel}
                    onEditChannelSubmit={handleEditChannel}
                  >
                    <Suspense fallback={null}>
                      <VoiceChatPage />
                    </Suspense>
                  </WorkspaceLayout>
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          <Route
            path={ROUTES.COMMUNICATION_CENTER}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <CommunicationCenterPage />
              </WorkspaceLayout>
            }
          />

          {/* ── Momentum Routines ── */}
          <Route
            path={ROUTES.MOMENTUM_ROUTINES}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <MomentumRoutinesPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.MOMENTUM_ROUTINES_DETAIL}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <MomentumRoutinesPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          <Route
            path={ROUTES.MARKETPLACE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
                marketplaceModule={selectedMarketplaceModule}
                onMarketplaceInstall={handleMarketplaceSidebarInstall}
                onMarketplaceUninstall={handleMarketplaceSidebarUninstall}
                marketplaceIsProcessing={marketplaceSidebarProcessing}
                onCloseMarketplaceDetail={closeMarketplaceDetail}
              >
                <MarketplacePage
                  onModuleOpen={openMarketplaceDetail}
                  onModuleClose={closeMarketplaceDetail}
                />
              </WorkspaceLayout>
            }
          />

          <Route
            path={ROUTES.MAC_APPS}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <MacAppsPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Claude Agents ── */}
          <Route
            path={ROUTES.CLAUDE_AGENTS}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                  showToolLog={showToolLog}
                  toolLogEntries={toolLogEntries}
                  onCloseToolLog={closeToolLog}
                >
                  <ClaudeAgentsPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.CLAUDE_AGENT_CHAT}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                  showToolLog={showToolLog}
                  toolLogEntries={toolLogEntries}
                  onCloseToolLog={closeToolLog}
                >
                  <ClaudeAgentsPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Claude Agents — Skills full page ── */}
          <Route
            path={ROUTES.CLAUDE_AGENTS_SKILLS}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <ClaudeAgentSkillsPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Claude Agents — Agent config full page ── */}
          <Route
            path={ROUTES.CLAUDE_AGENTS_AGENT_CONFIG}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <ClaudeAgentAgentPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Claude Agents — MCP config full page ── */}
          <Route
            path={ROUTES.CLAUDE_AGENTS_MCP_CONFIG}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <ClaudeAgentMcpPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Settings — MCP Servers ── */}
          <Route
            path={ROUTES.SETTINGS_MCP}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <McpServersPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Settings — Webhooks ── */}
          <Route
            path={ROUTES.SETTINGS_WEBHOOKS}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <WebhooksPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Settings — Webhook Report ── */}
          <Route
            path={ROUTES.SETTINGS_WEBHOOKS_REPORT}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <WebhookReportPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Settings — Harness Type ── */}
          <Route
            path={ROUTES.SETTINGS_HARNESS_TYPE}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <HarnessTypeSettingsPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── My Day ── */}
          <Route
            path={ROUTES.MY_DAY}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <MyDayPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Admin Dashboard (agency admins) ── */}
          {/* One route for the list and a person's page, so moving between them
              doesn't remount the layout (and re-run the sidebar's calls). */}
          {[`${ROUTES.AGENCY_ADMIN}/*`].map((path) => (
          <Route
            key={path}
            path={path}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                  showCreateAgent={createAgentOpen}
                  onCloseCreateAgent={closeCreateAgent}
                >
                  <AgencyAdminPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />
          ))}

          {/* ── Review Queue -- super users and allowed emails only ── */}
          <Route
            path={ROUTES.REVIEW_QUEUE}
            element={
              isReviewQueueEnabled() ? (
                <ProtectedRoute>
                  <WorkspaceLayout
                    sidebarOpen={sidebarOpen}
                    rightSidebarOpen={rightSidebarOpen}
                    mainCollapsed={mainCollapsed}
                    onToggleSidebar={toggleSidebar}
                    onCloseSidebar={closeSidebar}
                    onToggleRightSidebar={toggleRightSidebar}
                    onToggleMainPanel={toggleMainPanel}
                    leftSidebarCollapsed={leftSidebarCollapsed}
                    onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                    onAddChannel={openChannelModal}
                    onAddAmp={navigateToCreateAmp}
                    onEditChannel={openEditChannel}
                    showAddChannel={channelModalOpen}
                    onCloseAddChannel={closeChannelModal}
                    onAddChannelSubmit={handleAddChannel}
                    editingChannel={editingChannel}
                    onEditChannelSubmit={handleEditChannel}
                  >
                    <ReviewQueuePage />
                  </WorkspaceLayout>
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* ── My Tools ── */}
          <Route
            path={ROUTES.MY_TOOLS}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <MyToolsPage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path={ROUTES.MY_TOOLS_LEGACY}
            element={<Navigate to={`${ROUTES.MY_TOOLS}${location.search}`} replace />}
          />

          {/* ── API Integration Wizard ── */}
          <Route path={ROUTES.API_INTEGRATION_NEW} element={<LegacyNewIntegrationRedirect />} />
          <Route path={ROUTES.API_INTEGRATION_EDIT} element={<LegacyEditIntegrationRedirect />} />
          <Route path="/tools-marketplace/api-integrations" element={<LegacyIntegrationListRedirect />} />

          {/* ── Share a Skill (magic link) ── */}
          <Route
            path={ROUTES.SHARE_RESOLVE}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <ShareResolvePage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Share an Agent (magic link) ── */}
          <Route
            path={ROUTES.SHARE_AGENT_RESOLVE}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <ShareAgentResolvePage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── Share a Command Center category (magic link) ── */}
          <Route
            path={ROUTES.SHARE_CATEGORY_RESOLVE}
            element={
              <ProtectedRoute>
                <WorkspaceLayout
                  sidebarOpen={sidebarOpen}
                  rightSidebarOpen={rightSidebarOpen}
                  mainCollapsed={mainCollapsed}
                  onToggleSidebar={toggleSidebar}
                  onCloseSidebar={closeSidebar}
                  onToggleRightSidebar={toggleRightSidebar}
                  onToggleMainPanel={toggleMainPanel}
                  leftSidebarCollapsed={leftSidebarCollapsed}
                  onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                  onAddChannel={openChannelModal}
                  onAddAmp={navigateToCreateAmp}
                  onEditChannel={openEditChannel}
                  showAddChannel={channelModalOpen}
                  onCloseAddChannel={closeChannelModal}
                  onAddChannelSubmit={handleAddChannel}
                  editingChannel={editingChannel}
                  onEditChannelSubmit={handleEditChannel}
                >
                  <ShareCategoryResolvePage />
                </WorkspaceLayout>
              </ProtectedRoute>
            }
          />

          {/* ── MAC Workflows -- hidden until isMacWorkflowsEnabled is turned back on ── */}
          <Route
            path={ROUTES.MAC_WORKFLOWS}
            element={
              isMacWorkflowsEnabled() ? (
                <ProtectedRoute>
                  <WorkspaceLayout
                    sidebarOpen={sidebarOpen}
                    rightSidebarOpen={rightSidebarOpen}
                    mainCollapsed={mainCollapsed}
                    onToggleSidebar={toggleSidebar}
                    onCloseSidebar={closeSidebar}
                    onToggleRightSidebar={toggleRightSidebar}
                    onToggleMainPanel={toggleMainPanel}
                    leftSidebarCollapsed={leftSidebarCollapsed}
                    onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                    onAddChannel={openChannelModal}
                    onAddAmp={navigateToCreateAmp}
                    onEditChannel={openEditChannel}
                    showAddChannel={channelModalOpen}
                    onCloseAddChannel={closeChannelModal}
                    onAddChannelSubmit={handleAddChannel}
                    editingChannel={editingChannel}
                    onEditChannelSubmit={handleEditChannel}
                  >
                    <MacAIChatPage />
                  </WorkspaceLayout>
                </ProtectedRoute>
              ) : (
                <Navigate to="/" replace />
              )
            }
          />

          {/* ── Home Owners ── */}
          <Route
            path={ROUTES.HOME_OWNERS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <HomeOwnersPage />
              </WorkspaceLayout>
            }
          />

          {/* ── Fenris Personal Line ── */}
          <Route
            path={ROUTES.FENRIS_PERSONAL_LINE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <FenrisPersonalLinePage />
              </WorkspaceLayout>
            }
          />

          {/* ── Policy Comparison ── */}
          <Route
            path={ROUTES.POLICY_COMPARISON}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <PolicyComparisonPage />
              </WorkspaceLayout>
            }
          />

          {/* ── Fenris Driver and Vehicle ── */}
          <Route
            path={ROUTES.FENRIS_DRIVER_VEHICLE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <FenrisDriverVehiclePage />
              </WorkspaceLayout>
            }
          />

        {/* ── Data Enrichment Commercial ── */}
        <Route
          path={ROUTES.DATA_ENRICHMENT_COMMERCIAL}
          element={
            <WorkspaceLayout
              sidebarOpen={sidebarOpen}
              rightSidebarOpen={rightSidebarOpen}
              mainCollapsed={mainCollapsed}
              onToggleSidebar={toggleSidebar}
              onCloseSidebar={closeSidebar}
              onToggleRightSidebar={toggleRightSidebar}
              onToggleMainPanel={toggleMainPanel}
              leftSidebarCollapsed={leftSidebarCollapsed}
              onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
              onAddChannel={openChannelModal}
              onAddAmp={navigateToCreateAmp}
              onEditChannel={openEditChannel}
              showAddChannel={channelModalOpen}
              onCloseAddChannel={closeChannelModal}
              onAddChannelSubmit={handleAddChannel}
              editingChannel={editingChannel}
              onEditChannelSubmit={handleEditChannel}
            >
              <DataEnrichmentCommercialPage />
            </WorkspaceLayout>
          }
        />

        {/* ── Fenris Personal Line ── */}
        <Route
          path={ROUTES.FENRIS_PERSONAL_LINE}
          element={
            <WorkspaceLayout
              sidebarOpen={sidebarOpen}
              rightSidebarOpen={rightSidebarOpen}
              mainCollapsed={mainCollapsed}
              onToggleSidebar={toggleSidebar}
              onCloseSidebar={closeSidebar}
              onToggleRightSidebar={toggleRightSidebar}
              onToggleMainPanel={toggleMainPanel}
              leftSidebarCollapsed={leftSidebarCollapsed}
              onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
              onAddChannel={openChannelModal}
              onAddAmp={navigateToCreateAmp}
              onEditChannel={openEditChannel}
              showAddChannel={channelModalOpen}
              onCloseAddChannel={closeChannelModal}
              onAddChannelSubmit={handleAddChannel}
              editingChannel={editingChannel}
              onEditChannelSubmit={handleEditChannel}
            >
              <FenrisPersonalLinePage />
            </WorkspaceLayout>
          }
        />
          {/* ── Policy Summary ── */}
          <Route
            path={ROUTES.POLICY_SUMMARY}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <PolicySummaryPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.DIGITAL_WORKFORCE}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <DigitalWorkforcePage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.DW_ARTIFACTS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <DwArtifactsPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.ARTIFACTS}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <ArtifactsPage />
              </WorkspaceLayout>
            }
          />
          <Route
            path={ROUTES.ARTIFACT_DETAIL}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <ArtifactDetailPage />
              </WorkspaceLayout>
            }
          />

          <Route
            path={ROUTES.DATA_QUALITY_SCAN}
            element={
              <WorkspaceLayout
                sidebarOpen={sidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                mainCollapsed={mainCollapsed}
                onToggleSidebar={toggleSidebar}
                onCloseSidebar={closeSidebar}
                onToggleRightSidebar={toggleRightSidebar}
                onToggleMainPanel={toggleMainPanel}
                leftSidebarCollapsed={leftSidebarCollapsed}
                onToggleLeftSidebarCollapse={toggleLeftSidebarCollapse}
                onAddChannel={openChannelModal}
                onAddAmp={navigateToCreateAmp}
                onEditChannel={openEditChannel}
                showAddChannel={channelModalOpen}
                onCloseAddChannel={closeChannelModal}
                onAddChannelSubmit={handleAddChannel}
                editingChannel={editingChannel}
                onEditChannelSubmit={handleEditChannel}
              >
                <DataQualityScanPage />
              </WorkspaceLayout>
            }
          />

          <Route path="/hubvalidation" element={<ValidationPage />} />
          <Route
            path={ROUTES.REQUEST_EARLY_ACCESS}
            element={<RequestEarlyAccessPage />}
          />
          <Route path={ROUTES.ADMIN} element={<AdminDashboardPage />} />

          <Route path="*" element={<Navigate to={ROUTES.START} replace />} />
        </Routes>

        <AppLoadingSkeleton visible={showWelcomeSkeleton} />

        {showFloatingDock && (
          <div className="floating-dock">
            <ChatInput onSend={handleFloatingDockSend} />
          </div>
        )}
      </div>
      </ActiveAgentProvider>
    </ClaudeAgentsProvider>
  );
};

export default AppShell;
